"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { useSelector } from "@jti/store/reduxHooks";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { ConsentReminderDialog } from "@jti/ui/components/ConsentReminderDialog";
import { InputEditButton, TextInput } from "@jti/ui/components/Inputs";
import { useCurrentBreakpoint } from "@jti/ui/hooks/useCurrentBreakpoint";

import type {
  OnsitePersonalizationModalProps,
  OnsitePersonalizationModalTrigger,
} from "@checkout/components/OnsitePersonalizationModal/types";

import { useExitIntent } from "../hooks/useExitIntent";
import { useInactivity } from "../hooks/useInactivity";
import { isWithinLocalTimeWindow } from "../hooks/useLocalTimeWindow";
import { usePurchasedXDaysAgo } from "../hooks/usePurchasedXDaysAgo";
import { useQuickSwipeUp } from "../hooks/useQuickSwipeUp";
import { useScrollDepth } from "../hooks/useScrollDepth";
import { useTimeOnPage } from "../hooks/useTimeOnPage";
import { useTriggerCoordinator } from "../hooks/useTriggerCoordinator";
import { useUserActionTrigger } from "../hooks/useUserActionTrigger";
import { useUserActivityTracking } from "../hooks/useUserActivityTracking";
import { useWelcomeQueue } from "../hooks/useWelcomeQueue";
import { recordPopupShown, shouldShowPopup } from "../utils";

import { useOnsitePersonalizationPriority } from "./OnsitePersonalizationModalPriorityProvider";

import styles from "./OnsitePersonalizationModal.module.scss";

const ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION = "onsite-personalization-modal";
const CONSENT_REMINDER_DIALOG_TEST_ID = "consentReminderDialog";
const POST_AUTH_TRIGGERS = new Set(["loginSuccess", "completeRegistration"]);
const RECENT_ACTIVITY_WINDOW_MS = 5000;

type PopupOpenTrigger = OnsitePersonalizationModalTrigger;
type PopinCloseReason = "outside_click" | "x_button";
type PopinAcknowledgementStatus = "acknowledged" | "not acknowledged";

const ACTIVE_POPIN_TRIGGERS = new Set<PopupOpenTrigger>([
  "addToCart",
  "completeRegistration",
  "exitIntent",
  "loginSuccess",
  "placeOrder",
  "quickSwipeUp",
  "removeFromCart",
  "scrollDepth",
]);

const getTrackedLinkLabel = (link: HTMLAnchorElement) =>
  link.textContent?.trim() || link.getAttribute("aria-label") || link.getAttribute("title") || link.href;

const isDocumentFocused = () => typeof document.hasFocus !== "function" || document.hasFocus();

const { sanitize } = dompurify;
const getCurrentPageKey = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.location.pathname;
};

export const OnsitePersonalizationModal = ({
  mainImage,
  mobileImage,
  imageAltText,
  title,
  primaryButtonText,
  secondaryButtonText,
  description,
  subtitle,
  paragraph,
  coupon,
  copyCouponLabel,
  couponHelperText,
  isAuthorMode = false,
  isImageOnLeft = true,
  isCouponAvailable = false,
  inactivityDelay = 20,
  timeOnPageDelay = 5,
  hideModalExpireTime,
  isInactivityModalEnabled,
  isPurchasedDaysAgoModalEnabled,
  purchasedDaysAgoDelay = 7,
  isQuickSwipeUpModalEnabled,
  triggerMode = "or",
  isScrollDepthModalEnabled,
  isExitIntentEnabled = false,
  isTimeOnPageEnabled,
  minCartItemCount = 1,
  priority = 1,
  primaryButtonUrl,
  secondaryButtonUrl,
  popupId,
  frequencyType,
  frequencyValue,
  isUserActionTriggerEnabled,
  userActionTrigger,
  isLocalTimeTriggerEnabled,
  localTimeWindow,
  customStartHour,
  customEndHour,
}: OnsitePersonalizationModalProps) => {
  const config = useConfig();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const lastUserActivityAtRef = useUserActivityTracking();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const cart = useSelector((state: { cart: { items?: unknown[] } }) => state.cart);
  const { mainBreakpoint } = useCurrentBreakpoint();
  const { requestDisplay } = useOnsitePersonalizationPriority();

  const isMobile = useMemo(() => ["xs", "sm"].includes(mainBreakpoint), [mainBreakpoint]);
  const modalImage = isMobile && mobileImage ? mobileImage : mainImage;

  const frequencyConfig = useMemo(
    () => ({
      frequencyType,
      frequencyValue,
      hideModalExpireTime,
    }),
    [frequencyType, frequencyValue, hideModalExpireTime]
  );
  const canOpenModalWithPurchasedDaysAgo = usePurchasedXDaysAgo(isPurchasedDaysAgoModalEnabled, purchasedDaysAgoDelay);

  const getAcknowledgementStatus = useCallback(
    (trigger: PopupOpenTrigger): PopinAcknowledgementStatus => {
      if (document.visibilityState !== "visible") {
        return "not acknowledged";
      }

      const isRecentlyActive =
        Date.now() - lastUserActivityAtRef.current <= Math.min(inactivityDelay * 1000, RECENT_ACTIVITY_WINDOW_MS);

      if (trigger === "inactivity") {
        return isRecentlyActive ? "acknowledged" : "not acknowledged";
      }

      if (ACTIVE_POPIN_TRIGGERS.has(trigger)) {
        return "acknowledged";
      }

      return isDocumentFocused() || isRecentlyActive ? "acknowledged" : "not acknowledged";
    },
    [inactivityDelay, lastUserActivityAtRef]
  );

  const openModal = useCallback(
    async (trigger: PopupOpenTrigger = "default") => {
      if (isModalOpened) {
        return;
      }

      const isPostAuthTrigger = trigger ? POST_AUTH_TRIGGERS.has(trigger) : false;

      if (!isPostAuthTrigger && (cart?.items?.length ?? 0) < minCartItemCount) {
        return;
      }

      if (isLocalTimeTriggerEnabled && !isWithinLocalTimeWindow(localTimeWindow, customStartHour, customEndHour)) {
        return;
      }

      if (!canOpenModalWithPurchasedDaysAgo) {
        return;
      }

      if (!shouldShowPopup(frequencyConfig, popupId, getCurrentPageKey())) {
        return;
      }

      const won = await requestDisplay(priority);
      if (!won) {
        return;
      }

      setIsModalOpened(true);
      dispatchTrackingEvent(TrackingEvents.TrackingPopinView, {
        acknowledgement_status: getAcknowledgementStatus(trigger),
        popin_location: ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION,
        popin_name: title,
      });
    },
    [
      isModalOpened,
      cart?.items,
      minCartItemCount,
      frequencyConfig,
      popupId,
      requestDisplay,
      priority,
      isLocalTimeTriggerEnabled,
      localTimeWindow,
      customStartHour,
      customEndHour,
      canOpenModalWithPurchasedDaysAgo,
      getAcknowledgementStatus,
      title,
    ]
  );

  const openModalWithWelcomeQueue = useWelcomeQueue(openModal);

  const { onTrigger } = useTriggerCoordinator({
    triggers: {
      inactivity: isInactivityModalEnabled,
      quickSwipeUp: isQuickSwipeUpModalEnabled,
      timeOnPage: isTimeOnPageEnabled,
      exitIntent: isExitIntentEnabled,
      scrollDepth: isScrollDepthModalEnabled,
    },
    isModalOpened,
    onAllTriggered: (key) => openModalWithWelcomeQueue(key),
    mode: triggerMode,
  });

  useInactivity(() => onTrigger("inactivity"), inactivityDelay, isInactivityModalEnabled, isModalOpened);
  useQuickSwipeUp(() => onTrigger("quickSwipeUp"), isQuickSwipeUpModalEnabled, isModalOpened);
  useScrollDepth(() => onTrigger("scrollDepth"), isModalOpened, isScrollDepthModalEnabled);
  useExitIntent(() => onTrigger("exitIntent"), isExitIntentEnabled, isModalOpened);
  useTimeOnPage(() => onTrigger("timeOnPage"), timeOnPageDelay, isTimeOnPageEnabled, isModalOpened);

  useUserActionTrigger(openModalWithWelcomeQueue, userActionTrigger, isUserActionTriggerEnabled, isModalOpened);

  const handlePopinClose = useCallback(
    (closeReason: PopinCloseReason) => {
      dispatchTrackingEvent(TrackingEvents.TrackingPopinClose, {
        close_reason: closeReason,
        popin_location: ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION,
        popin_name: title,
      });
    },
    [title]
  );

  const handleCouponCopy = () => {
    if (isCouponAvailable && coupon) {
      navigator.clipboard.writeText(coupon);
    }
  };

  const handleOnCloseModal = useCallback(
    (closeReason: PopinCloseReason = "x_button") => {
      setIsModalOpened(false);
      recordPopupShown(frequencyConfig, popupId, getCurrentPageKey());
      handlePopinClose(closeReason);
    },
    [frequencyConfig, handlePopinClose, popupId]
  );

  useEffect(() => {
    if (!isModalOpened) {
      return;
    }

    const dialogElement = document.querySelector<HTMLDialogElement>(
      `[data-testid="${CONSENT_REMINDER_DIALOG_TEST_ID}"]`
    );

    if (!dialogElement) {
      return;
    }

    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target !== dialogElement) {
        return;
      }

      handleOnCloseModal("outside_click");
    };

    dialogElement.addEventListener("click", handleBackdropClick);

    return () => {
      dialogElement.removeEventListener("click", handleBackdropClick);
    };
  }, [handleOnCloseModal, isModalOpened]);

  useEffect(() => {
    if (!isModalOpened) {
      return;
    }

    const contentElement = modalContentRef.current;

    if (!contentElement) {
      return;
    }

    const handleContentLinkClick = (event: MouseEvent) => {
      const eventTarget = event.target as Element | null;
      const targetElement = eventTarget instanceof Element ? eventTarget : null;
      const linkElement = targetElement?.closest("a[href]");

      if (!(linkElement instanceof HTMLAnchorElement)) {
        return;
      }

      dispatchTrackingEvent(TrackingEvents.TrackingPopinLinkClick, {
        link_text: getTrackedLinkLabel(linkElement),
        link_url: linkElement.href,
        popin_location: ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION,
        popin_name: title,
      });
    };

    contentElement.addEventListener("click", handleContentLinkClick);

    return () => {
      contentElement.removeEventListener("click", handleContentLinkClick);
    };
  }, [isModalOpened, title]);

  const handlePrimaryButtonClick = useCallback(() => {
    recordPopupShown(frequencyConfig, popupId, getCurrentPageKey());
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: primaryButtonText,
      cta_location: ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION,
    });
    if (!primaryButtonUrl) {
      window.location.href = config.CART_CHECKOUT_PAGE_URL;
    }
  }, [frequencyConfig, popupId, primaryButtonText, primaryButtonUrl, config.CART_CHECKOUT_PAGE_URL]);

  const handleSecondaryButtonClick = useCallback(() => {
    recordPopupShown(frequencyConfig, popupId, getCurrentPageKey());
    if (secondaryButtonText) {
      dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
        cta_name: secondaryButtonText,
        cta_location: ONSITE_PERSONALIZATION_MODAL_CTA_LOCATION,
      });
    }
    if (!secondaryButtonUrl) {
      setIsModalOpened(false);
    }
  }, [frequencyConfig, popupId, secondaryButtonText, secondaryButtonUrl]);

  return (
    <ConsentReminderDialog
      isOpened={isModalOpened || isAuthorMode}
      mainImage={modalImage}
      imageAltText={imageAltText}
      title={title}
      description={description}
      subtitle={subtitle}
      primaryButtonText={primaryButtonText}
      primaryButtonHref={primaryButtonUrl}
      secondaryButtonText={secondaryButtonText}
      secondaryButtonHref={secondaryButtonUrl}
      closable={true}
      onClose={() => handleOnCloseModal("x_button")}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      onSecondaryButtonClick={handleSecondaryButtonClick}
      isImageOnLeft={isImageOnLeft}
    >
      {paragraph && (
        <div
          data-testid="onsite-personalization-modal-content"
          ref={modalContentRef}
          dangerouslySetInnerHTML={{ __html: sanitize(paragraph) }}
        />
      )}
      {isCouponAvailable && coupon && (
        <div className={styles.personalizationModalCouponWrapper} data-testid="copyCoupon">
          <TextInput
            id="coupon-display"
            readOnly
            value={coupon}
            aria-label={couponHelperText}
            helperText={couponHelperText}
            elemAfter={<InputEditButton label={copyCouponLabel} onClick={handleCouponCopy} />}
          />
        </div>
      )}
    </ConsentReminderDialog>
  );
};
